import React, { useEffect, useMemo, useState } from "react";
import Control from "../../common/Control";
import DynamicList from "../../common/DynamicList";
import PostalCode from "../../common/PostalCode";
import ModaleLocale from "../../common/CustomModal/ModaleLocale";
import useStore from "../../../context/useStore";
import { creerSite, modifierSite } from "./operateursApi";

// ============================================================
// Création et modification d'un site d'opérateur (routes /sites existantes,
// clés en anglais, avec `operator` à la place de `organization`, plus phone,
// email, link et subThemes). Pour un opérateur sans régime, le site est une
// simple adresse : ni box ni commune desservie. Avec un régime, le site a un
// calendrier et exige au moins un box et une commune, comme un site Caf.
// Pas de contacts nominatifs : inutiles sur un site d'opérateur.
// Erreurs 400/419 sous les champs, refus en bandeau.
// ============================================================

// Zones de La Poste : par code postal, la zone sans quartier (les autres
// adresses du code postal) en tête du sien.
const ordreZone = (a, b) =>
  String(a.postalCode).localeCompare(String(b.postalCode)) ||
  Boolean(a.locationName) - Boolean(b.locationName);

export const regrouperParCommune = (villes = []) => {
  const communes = [];
  villes.forEach((ville) => {
    let commune = communes.find((c) => c.townName === ville.townName);
    if (!commune) {
      commune = { townName: ville.townName, postalCodes: [] };
      communes.push(commune);
    }
    commune.postalCodes.push(ville);
  });
  communes.forEach((commune) => commune.postalCodes.sort(ordreZone));
  return communes.sort((a, b) => a.townName.localeCompare(b.townName, "fr"));
};

// La zone sans quartier n'est pas toute la commune : « autres quartiers » dès
// que la commune a d'autres zones.
export const libelleZone = (zone, commune) => {
  if (zone.locationName) return `${zone.postalCode} · ${zone.locationName}`;
  const autres = commune.postalCodes.length > 1 ? ", autres quartiers" : "";
  return `${zone.postalCode} · ${commune.townName}${autres}`;
};

const normaliser = (texte) =>
  String(texte || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[-'’]/g, " ");

const SITE_VIDE = {
  name: "",
  address: "",
  cityId: null,
  location: null,
  phone: "",
  email: "",
  link: "",
  subThemes: [],
  boxes: [{ name: "Box 1" }],
  locations: [],
  closingPeriods: [],
  closingHolidays: [],
};

const formulaireDepuisSite = (site, feries) => {
  if (!site) return { ...SITE_VIDE, closingHolidays: feries.map((f) => String(f.id)) };
  return {
    name: site.name || "",
    address: site.address || "",
    cityId: (site.location && site.location.cityId) || null,
    location: site.location || null,
    phone: site.phone || "",
    email: site.email || "",
    link: site.link || "",
    subThemes: site.subThemes || [],
    boxes: (site.boxes || []).filter((b) => !b.isDeleted),
    locations: site.locations || [],
    closingPeriods: site.closingPeriods || [],
    closingHolidays: (site.closingHolidays || []).map(String),
  };
};

const ModaleSite = ({ ouvert, onFermer, operateur, site = null, villes, themes, onEnregistre }) => {
  const [state] = useStore();
  const feries = (state.constants.items && state.constants.items.HOLIDAYS) || [];
  const [formulaire, setFormulaire] = useState(() => formulaireDepuisSite(site, feries));
  const [erreurs, setErreurs] = useState({});
  const [refus, setRefus] = useState(null);
  const [enregistrement, setEnregistrement] = useState(false);
  const [filtreCommunes, setFiltreCommunes] = useState("");
  const [communeDetail, setCommuneDetail] = useState("");
  const [nouveauBox, setNouveauBox] = useState("");

  const avecCalendrier = (operateur.regimes || []).length > 0;
  const communes = useMemo(() => regrouperParCommune(villes), [villes]);

  useEffect(() => {
    if (ouvert) {
      setFormulaire(formulaireDepuisSite(site, feries));
      setErreurs({});
      setRefus(null);
      setFiltreCommunes("");
      setCommuneDetail("");
      setNouveauBox("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ouvert, site]);

  const changer = (e) => {
    const { name, value, other } = e.target;
    setFormulaire((f) =>
      name === "cityId" ? { ...f, cityId: value, location: other || f.location } : { ...f, [name]: value }
    );
    setErreurs((er) => ({ ...er, [name]: undefined }));
  };

  const basculerDans = (nom, valeur) => {
    setFormulaire((f) => ({
      ...f,
      [nom]: f[nom].includes(valeur) ? f[nom].filter((v) => v !== valeur) : [...f[nom], valeur],
    }));
    setErreurs((er) => ({ ...er, [nom]: undefined }));
  };

  // Communes desservies : la carte coche ou décoche tous les codes postaux de
  // la commune ; le détail par code postal reste possible dans le bloc replié.
  const idsCommune = (commune) => commune.postalCodes.map((v) => v.id);
  const nbCoches = (commune) => idsCommune(commune).filter((id) => formulaire.locations.includes(id)).length;
  const basculerCommune = (commune) => {
    const ids = idsCommune(commune);
    const toutCoche = nbCoches(commune) === ids.length;
    setFormulaire((f) => ({
      ...f,
      locations: toutCoche
        ? f.locations.filter((id) => !ids.includes(id))
        : [...f.locations, ...ids.filter((id) => !f.locations.includes(id))],
    }));
    setErreurs((er) => ({ ...er, locations: undefined }));
  };
  // « Tout cocher » porte sur les communes affichées (donc sur le filtre s'il y
  // en a un) ; quand elles sont toutes cochées, le bouton les décoche.
  const communesFiltrees = communes.filter(
    (c) => !filtreCommunes || normaliser(c.townName).includes(normaliser(filtreCommunes))
  );
  const idsAffiches = communesFiltrees.flatMap(idsCommune);
  const toutAfficheCoche = idsAffiches.length > 0 && idsAffiches.every((id) => formulaire.locations.includes(id));
  const basculerToutesLesCommunes = () => {
    setFormulaire((f) => ({
      ...f,
      locations: toutAfficheCoche
        ? f.locations.filter((id) => !idsAffiches.includes(id))
        : [...f.locations, ...idsAffiches.filter((id) => !f.locations.includes(id))],
    }));
    setErreurs((er) => ({ ...er, locations: undefined }));
  };

  const ajouterBox = () => {
    const nom = nouveauBox.trim() || `Box ${formulaire.boxes.length + 1}`;
    setFormulaire((f) => ({ ...f, boxes: [...f.boxes, { name: nom }] }));
    setNouveauBox("");
    setErreurs((er) => ({ ...er, boxes: undefined }));
  };
  const retirerBox = (index) => {
    setFormulaire((f) => ({ ...f, boxes: f.boxes.filter((_, k) => k !== index) }));
  };

  const enregistrer = (e) => {
    e.preventDefault();
    const corps = {
      operator: operateur.id,
      name: formulaire.name.trim(),
      address: formulaire.address.trim(),
      cityId: formulaire.cityId,
      phone: formulaire.phone.trim() || null,
      email: formulaire.email.trim() || null,
      link: formulaire.link.trim() || null,
      subThemes: formulaire.subThemes,
      boxes: avecCalendrier ? formulaire.boxes : [],
      locations: avecCalendrier ? formulaire.locations : [],
      closingPeriods: formulaire.closingPeriods,
      closingHolidays: formulaire.closingHolidays,
    };
    setEnregistrement(true);
    setErreurs({});
    setRefus(null);
    const appel = site ? modifierSite(site.id, corps) : creerSite(corps);
    appel.then(
      (siteEnregistre) => {
        setEnregistrement(false);
        onEnregistre(siteEnregistre, site ? "modification" : "creation");
      },
      (erreur) => {
        setEnregistrement(false);
        if (erreur.champs) setErreurs(erreur.champs);
        else setRefus(erreur.message || "Une erreur est survenue");
      }
    );
  };

  const communeDetaillee = communes.find((c) => c.townName === communeDetail);

  return (
    <ModaleLocale
      ouvert={ouvert}
      onFermer={onFermer}
      titre={site ? `Modifier le site ${site.name}` : `Ajouter un site à ${operateur.nom}`}
      sousTitre={
        avecCalendrier
          ? "Le site reçoit son calendrier dès sa création."
          : "Une adresse du répertoire : sans régime, le site n'a ni box ni calendrier."
      }
      taille="moyenne"
      idTitre="modale-site"
      pied={
        <>
          <button type="button" className="btn btn-ghost" onClick={onFermer}>
            Annuler
          </button>
          <button
            type="submit"
            form="formulaire-site"
            className="btn btn-primary"
            disabled={enregistrement}
          >
            {site ? "Enregistrer le site" : "Créer le site"}
          </button>
        </>
      }
    >
      <form id="formulaire-site" onSubmit={enregistrer} noValidate>
        {refus && (
          <div className="alert alert-danger" role="alert">
            {refus}
          </div>
        )}

        <div className="modale-operateur__champs">
          <Control
            margin="my-0 modale-operateur__champ-large"
            label="Nom du site"
            name="name"
            value={formulaire.name}
            change={changer}
            error={erreurs}
            required
            inputProps={{ autoComplete: "off" }}
          />
          <Control
            margin="my-0 modale-operateur__champ-large"
            label="Adresse"
            name="address"
            value={formulaire.address}
            change={changer}
            error={erreurs}
            required
            inputProps={{ autoComplete: "off" }}
          />
          <PostalCode
            inputLabel="Commune du site"
            value={formulaire.cityId}
            name="cityId"
            inputId="site-code-postal"
            updateData={changer}
            error={erreurs}
            location={formulaire.location}
          />
          <Control
            margin="my-0"
            label="Téléphone"
            type="tel"
            name="phone"
            value={formulaire.phone}
            change={changer}
            error={erreurs}
          />
          <Control
            margin="my-0"
            label="E-mail"
            type="email"
            name="email"
            value={formulaire.email}
            change={changer}
            error={erreurs}
          />
          <Control
            margin="my-0"
            label="Site internet"
            name="link"
            placeholder="https://"
            value={formulaire.link}
            change={changer}
            error={erreurs}
          />
        </div>

        <fieldset className="modale-operateur__section">
          <legend className="modale-operateur__legende">Activités proposées sur ce site</legend>
          <p className="modale-operateur__aide">
            Les sous-thèmes cochés sont proposés dans les étapes des contrats d'engagement.
          </p>
          {themes.map((theme) => {
            const coches = theme.subThemes.filter((st) => formulaire.subThemes.includes(st._id)).length;
            return (
              <details className="replie" key={theme.id || theme.name}>
                <summary>
                  <i className="fa fa-chevron-right" aria-hidden="true" />
                  {theme.name}
                  {coches > 0 && <span className="compteur-replie">{coches}</span>}
                </summary>
                <div className="choix-compacts">
                  {theme.subThemes.map((sousTheme) => (
                    <label key={sousTheme._id} className="choix-carte choix-compacte">
                      <input
                        type="checkbox"
                        checked={formulaire.subThemes.includes(sousTheme._id)}
                        onChange={() => basculerDans("subThemes", sousTheme._id)}
                      />
                      <span>{sousTheme.name}</span>
                    </label>
                  ))}
                </div>
              </details>
            );
          })}
          {erreurs.subThemes && (
            <small role="alert" className="form-text text-danger">
              {erreurs.subThemes}
            </small>
          )}
        </fieldset>

        {avecCalendrier && (
          <>
            <fieldset className="modale-operateur__section">
              <legend className="modale-operateur__legende">Box de réception</legend>
              <div className="puces-box">
                {formulaire.boxes.map((box, k) => (
                  <span className="puce-box" key={`${box.name}-${k}`}>
                    {box.name}
                    <button
                      type="button"
                      aria-label={`Retirer ${box.name}`}
                      onClick={() => retirerBox(k)}
                    >
                      <i className="fa fa-times" aria-hidden="true" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="ajout-box">
                <label className="sr-only" htmlFor="nouveau-box">
                  Nom du box à ajouter
                </label>
                <input
                  id="nouveau-box"
                  className="form-control"
                  value={nouveauBox}
                  placeholder={`Box ${formulaire.boxes.length + 1}`}
                  onChange={(e) => setNouveauBox(e.target.value)}
                />
                <button type="button" className="btn btn-ghost" onClick={ajouterBox}>
                  <i className="fa fa-plus mr-2" aria-hidden="true" />
                  Ajouter un box
                </button>
              </div>
              <p className="modale-operateur__aide">
                Un box par agent qui reçoit en même temps : le calendrier propose un créneau par
                box. Un box retiré alors que des créneaux y sont rattachés est archivé, pas
                supprimé.
              </p>
              {erreurs.boxes && (
                <small role="alert" className="form-text text-danger">
                  {erreurs.boxes}
                </small>
              )}
            </fieldset>

            <fieldset className="modale-operateur__section">
              <legend className="modale-operateur__legende">Communes desservies</legend>
              <div className="barre-communes">
                <span className="compteur-communes" aria-live="polite">
                  {formulaire.locations.length} zone{formulaire.locations.length > 1 ? "s" : ""}
                </span>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm tout-cocher-communes"
                  onClick={basculerToutesLesCommunes}
                  disabled={idsAffiches.length === 0}
                >
                  {toutAfficheCoche ? "Tout décocher" : "Tout cocher"}
                </button>
                <label className="filtre-communes">
                  <i className="fa fa-search" aria-hidden="true" />
                  <span className="sr-only">Filtrer les communes</span>
                  <input
                    type="search"
                    value={filtreCommunes}
                    placeholder="Filtrer les communes…"
                    onChange={(e) => setFiltreCommunes(e.target.value)}
                  />
                </label>
              </div>
              <p className="modale-operateur__aide">
                Les allocataires de ces communes sont reçus sur ce site.
              </p>
              <div className="choix-compacts choix-compacts--communes">
                {communesFiltrees.map((commune) => {
                  const total = commune.postalCodes.length;
                  const coches = nbCoches(commune);
                  return (
                    <label key={commune.townName} className="choix-carte choix-compacte">
                      <input
                        type="checkbox"
                        checked={coches === total && total > 0}
                        ref={(el) => {
                          if (el) el.indeterminate = coches > 0 && coches < total;
                        }}
                        onChange={() => basculerCommune(commune)}
                      />
                      <span>
                        {commune.townName}
                        {coches > 0 && coches < total && (
                          <span className="choix-compacte__detail">
                            {coches} / {total} zones
                          </span>
                        )}
                      </span>
                    </label>
                  );
                })}
              </div>
              {erreurs.locations && (
                <small role="alert" className="form-text text-danger">
                  {erreurs.locations}
                </small>
              )}
              <details className="replie">
                <summary>
                  <i className="fa fa-chevron-right" aria-hidden="true" />
                  Affiner par code postal (facultatif)
                </summary>
                <div>
                  <Control
                    margin="my-0"
                    label="Commune"
                    type="select"
                    name="communeDetail"
                    value={communeDetail}
                    datas={communes.map((c) => ({ id: c.townName, name: c.townName }))}
                    change={(e) => setCommuneDetail(e.target.value)}
                    selectFirstLabel="Choisir une commune"
                  />
                  {communeDetaillee && (
                    <div className="choix-compacts">
                      {communeDetaillee.postalCodes.map((zone) => (
                        <label key={zone.id} className="choix-carte choix-compacte">
                          <input
                            type="checkbox"
                            checked={formulaire.locations.includes(zone.id)}
                            onChange={() => basculerDans("locations", zone.id)}
                          />
                          <span>{libelleZone(zone, communeDetaillee)}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </details>
            </fieldset>
          </>
        )}

        <fieldset className="modale-operateur__section">
          <legend className="modale-operateur__legende">Fermetures</legend>
          <details className="replie">
            <summary>
              <i className="fa fa-chevron-right" aria-hidden="true" />
              Jours fériés et périodes de fermeture
            </summary>
            <div>
              <p className="modale-operateur__aide">
                Le site est fermé les jours fériés cochés ; aucun créneau n'y est proposé.
              </p>
              <div className="choix-compacts">
                {feries.map((ferie) => (
                  <label key={ferie.id} className="choix-carte choix-compacte">
                    <input
                      type="checkbox"
                      checked={formulaire.closingHolidays.includes(String(ferie.id))}
                      onChange={() => basculerDans("closingHolidays", String(ferie.id))}
                    />
                    <span>{ferie.name}</span>
                  </label>
                ))}
              </div>
              <DynamicList
                listLabel="Périodes de fermeture"
                btnLabel="Ajouter une période"
                uniqueKey="site-fermetures"
                name="closingPeriods"
                fields={[
                  { label: "Nom", name: "name", type: "text", placeholder: "Fermeture annuelle" },
                  { label: "Entre le", name: "beginAt", type: "date" },
                  { label: "et le", name: "endAt", type: "date" },
                ]}
                items={formulaire.closingPeriods}
                change={changer}
                error={erreurs}
              />
            </div>
          </details>
        </fieldset>
      </form>
    </ModaleLocale>
  );
};

export default ModaleSite;
